/* CMS Schemas — one per collection / module.
 *
 * Each schema describes:
 *   - title:        Human-friendly module name (sidebar + breadcrumb)
 *   - collection:   MongoDB collection name (matches backend whitelist)
 *   - singleton:    If true, the module is a single document (e.g. site_settings, founder)
 *   - assetCategory:Default asset library category when uploading from this module
 *   - labelField:   Function (item) => string. Renders the list row label.
 *   - sublabelField:Function (item) => string. Renders the list row sublabel.
 *   - statusOptions:Optional list of valid statuses (drives the dropdown)
 *   - sections:     Ordered array of { title, fields: [Field] }
 *
 * Field shape:
 *   { key, label, type, help?, options?, required?, keys?, category?, defaultItem?, rowFields?, rows? }
 *   type ∈ "text" | "url" | "email" | "slug" | "number" | "textarea" | "longtext"
 *         | "select" | "switch" | "image" | "linkmap" | "rows" | "tags"
 */

const STATUS_OPTIONS = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft (hidden)" },
  { value: "placeholder", label: "Placeholder (visible with badge)" },
];

const BOOK_STATUS_OPTIONS = [
  { value: "published", label: "Published — retail links live" },
  { value: "released_pending_retail", label: "Released — retail link pending" },
  { value: "in_review", label: "In Review" },
  { value: "coming_soon", label: "Coming Soon" },
  { value: "under_publication", label: "Under Publication" },
  { value: "draft", label: "Draft (hidden)" },
];

const PROGRAM_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "coming_soon", label: "Coming Soon" },
  { value: "planned", label: "Planned" },
  { value: "placeholder", label: "Placeholder" },
  { value: "draft", label: "Draft (hidden)" },
];

const MEDIA_TYPE_OPTIONS = [
  { value: "article", label: "Article" },
  { value: "talk", label: "Talk" },
  { value: "interview", label: "Interview" },
  { value: "podcast", label: "Podcast" },
  { value: "press", label: "Press" },
  { value: "book_launch", label: "Book Launch" },
  { value: "gallery", label: "Gallery" },
  { value: "recognition", label: "Recognition" },
];

/* ============ Site Settings ============ */
export const SITE_SETTINGS_SCHEMA = {
  title: "Site Settings",
  collection: "site_settings",
  singleton: true,
  singletonKey: { field: "key", value: "main" },
  assetCategory: "logo",
  sections: [
    {
      title: "Identity",
      fields: [
        { key: "organization_name", label: "Website name", type: "text", required: true },
        { key: "short_name", label: "Short name / abbreviation", type: "text" },
        { key: "motto", label: "Motto / tagline", type: "text" },
        { key: "tagline", label: "Long tagline / one-liner", type: "textarea" },
      ],
    },
    {
      title: "Contact",
      fields: [
        { key: "public_email", label: "Public email (visible on the site)", type: "email", required: true, help: "Used in footer and Contact form. Keep as info@bscalliance.org." },
        { key: "primary_email_placeholder", label: "Email placeholder in forms", type: "email" },
        { key: "admin_email", label: "Admin email (CMS only — not shown publicly)", type: "email", help: "Used only for CMS access. NEVER rendered on the public website." },
      ],
    },
    {
      title: "Brand assets",
      fields: [
        { key: "logo_assets.primary", label: "Header logo (primary)", type: "image", category: "logo" },
        { key: "logo_assets.dark_emblem", label: "Footer / dark emblem", type: "image", category: "logo" },
        { key: "logo_assets.favicon", label: "Favicon (.ico)", type: "image", category: "logo" },
        { key: "logo_assets.apple_touch", label: "Apple touch icon (180×180)", type: "image", category: "logo" },
        { key: "logo_assets.android_chrome_192", label: "Android Chrome icon (192×192)", type: "image", category: "logo" },
      ],
    },
    {
      title: "Footer",
      fields: [
        { key: "footer_text", label: "Footer copyright text", type: "text", help: "Example: © Beyond Science & Consciousness Alliance (BSCA). All rights reserved." },
      ],
    },
    {
      title: "Default SEO",
      fields: [
        { key: "seo.title", label: "Default SEO title", type: "text" },
        { key: "seo.description", label: "Default SEO description", type: "textarea" },
        { key: "seo.og_image", label: "Default Open Graph image", type: "image", category: "other" },
      ],
    },
    {
      title: "Social links",
      fields: [
        {
          key: "social_links",
          label: "Social media URLs",
          type: "linkmap",
          help: "Leave blank to hide. URLs only.",
          keys: [
            { value: "linkedin", label: "LinkedIn" },
            { value: "youtube", label: "YouTube" },
            { value: "instagram", label: "Instagram" },
            { value: "twitter", label: "X / Twitter" },
            { value: "facebook", label: "Facebook" },
            { value: "other", label: "Other" },
          ],
        },
      ],
    },
    {
      title: "Email notifications (Resend)",
      fields: [
        { key: "email.enabled", label: "Send email notifications", type: "switch", help: "When ON, every form submission triggers an admin notification + user acknowledgement via Resend." },
        { key: "email.admin_recipient", label: "Admin notification email", type: "email", help: "Where new-submission emails are delivered. Default: info@bscalliance.org" },
        { key: "email.sender_name", label: "Sender name", type: "text", help: "Default: Science + Consciousness Alliance" },
        { key: "email.sender_email", label: "Sender email", type: "email", help: "Must be a Resend-verified address. Until bscalliance.org is verified, leave as onboarding@resend.dev." },
        { key: "email.reply_to", label: "Reply-to email", type: "email", help: "Default: info@bscalliance.org" },
        {
          key: "email.acknowledgement_subjects",
          label: "Per-form acknowledgement subjects (optional overrides)",
          type: "linkmap",
          help: "Free-text overrides per form type. Leave blank to use defaults.",
          keys: [
            { value: "membership", label: "Membership", placeholder: "Welcome to BSCA — your membership request has been received" },
            { value: "contribution", label: "Contribution", placeholder: "BSCA Contribution Received" },
            { value: "collaboration", label: "Collaboration", placeholder: "BSCA Collaboration Proposal Received" },
            { value: "contact", label: "Contact", placeholder: "BSCA Message Received" },
          ],
        },
        {
          key: "email.acknowledgement_bodies",
          label: "Per-form acknowledgement bodies (optional overrides)",
          type: "linkmap",
          help: "Plain text. Leave blank to use the default 'Thank you for contacting BSCA…' message.",
          keys: [
            { value: "membership", label: "Membership" },
            { value: "contribution", label: "Contribution" },
            { value: "collaboration", label: "Collaboration" },
            { value: "contact", label: "Contact" },
          ],
        },
      ],
    },
  ],
};

/* ============ Navigation ============ */
export const NAVIGATION_SCHEMA = {
  title: "Navigation / Menu",
  collection: "navigation",
  singleton: true,
  singletonKey: { field: "key", value: "primary" },
  sections: [
    {
      title: "Primary navigation items",
      fields: [
        {
          key: "items",
          label: "Menu items",
          type: "rows",
          defaultItem: { label: "", href: "/", visible: true, new_tab: false, order: 0 },
          rowFields: [
            { key: "label", label: "Menu label", type: "text" },
            { key: "href", label: "Page link / route", type: "text", help: "Use /alliance, /manifesto, /engage#join, https://…" },
            { key: "order", label: "Display order", type: "number" },
            { key: "visible", label: "Show in menu", type: "switch" },
            { key: "new_tab", label: "Open in new tab", type: "switch" },
          ],
        },
      ],
    },
    {
      title: "CTA button",
      fields: [
        { key: "cta.label", label: "CTA label", type: "text", help: "Default: Join BSCA" },
        { key: "cta.href", label: "CTA link", type: "text", help: "Default: /engage#join" },
        { key: "cta.visible", label: "Show CTA button", type: "switch" },
      ],
    },
  ],
};

/* ============ Page Content (Pages) ============ */
const PAGE_HERO_FIELDS = [
  { key: "hero.eyebrow", label: "Hero label / eyebrow", type: "text" },
  { key: "hero.headline", label: "Hero headline", type: "textarea", rows: 2 },
  { key: "hero.subheadline", label: "Hero subheadline", type: "textarea", rows: 3 },
  { key: "hero.founder_line", label: "Hero founder line", type: "textarea", rows: 2 },
  { key: "hero.primary_cta.label", label: "Primary CTA label", type: "text" },
  { key: "hero.primary_cta.href", label: "Primary CTA link", type: "text" },
  { key: "hero.secondary_cta.label", label: "Secondary CTA label", type: "text" },
  { key: "hero.secondary_cta.href", label: "Secondary CTA link", type: "text" },
  { key: "hero.quote", label: "Hero quote (optional)", type: "textarea" },
  { key: "hero.quote_attribution", label: "Hero quote attribution", type: "text" },
  { key: "hero.background_image", label: "Hero background image (optional)", type: "image", category: "page" },
];

const PAGE_SEO_FIELDS = [
  { key: "seo.title", label: "SEO title", type: "text" },
  { key: "seo.description", label: "SEO description", type: "textarea" },
  { key: "seo.og_image", label: "Open Graph image", type: "image", category: "page" },
];

export const PAGE_CONTENT_SCHEMA = {
  title: "Pages",
  collection: "page_content",
  assetCategory: "page",
  labelField: (d) => humanPageTitle(d.slug) || d.slug,
  sublabelField: (d) => `${d.slug} · ${d.status || "draft"}`,
  statusOptions: STATUS_OPTIONS,
  newDefaults: { status: "draft" },
  sections: [
    {
      title: "Page identity",
      fields: [
        { key: "slug", label: "Slug (URL)", type: "slug", required: true, help: "URL path — e.g. home, alliance, manifesto." },
        { key: "status", label: "Status", type: "select", options: STATUS_OPTIONS, required: true },
      ],
    },
    { title: "Hero", fields: PAGE_HERO_FIELDS },
    { title: "SEO", fields: PAGE_SEO_FIELDS },
  ],
};

function humanPageTitle(slug) {
  if (!slug) return "";
  return slug
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ============ Books ============ */
export const BOOKS_SCHEMA = {
  title: "Books / Beyond Science Series",
  collection: "books",
  assetCategory: "book_cover",
  labelField: (d) => d.title || d.slug,
  sublabelField: (d) => `Vol ${d.volume || "?"} · ${d.status || "draft"}`,
  statusOptions: BOOK_STATUS_OPTIONS,
  newDefaults: { status: "draft", retail_links: {} },
  sections: [
    {
      title: "Identity",
      fields: [
        { key: "title", label: "Title", type: "text", required: true },
        { key: "series_title", label: "Series title", type: "text", help: "e.g. Beyond Science" },
        { key: "volume", label: "Volume number", type: "number" },
        { key: "slug", label: "Slug (URL)", type: "slug", required: true, help: "e.g. edge-of-the-universe" },
        { key: "subtitle", label: "Subtitle / theme", type: "text" },
        { key: "core_question", label: "Core question", type: "textarea", rows: 2 },
        { key: "status", label: "Status", type: "select", options: BOOK_STATUS_OPTIONS, required: true },
        { key: "status_label", label: "Status label (free text shown on card)", type: "text" },
      ],
    },
    {
      title: "Cover & description",
      fields: [
        { key: "cover_image", label: "Cover image", type: "image", category: "book_cover" },
        { key: "cover_alt", label: "Cover alt text (accessibility)", type: "text" },
        { key: "cover_asset", label: "Cover asset reference (legacy)", type: "text", help: "Free-text reference. Use the Cover image field above to update the actual file." },
        { key: "cover_image_uploaded", label: "Cover uploaded?", type: "switch" },
        { key: "cover_upload_note", label: "Cover upload note (internal)", type: "textarea" },
        { key: "description", label: "Description", type: "longtext" },
      ],
    },
    {
      title: "Chapter highlights",
      fields: [
        {
          key: "chapter_highlights",
          label: "Chapter highlights (one per row)",
          type: "stringRows",
          itemLabel: "Highlight",
          addLabel: "Add highlight",
        },
      ],
    },
    {
      title: "Retail / reading links",
      fields: [
        {
          key: "retail_links",
          label: "Retail links",
          type: "linkmap",
          keys: [
            { value: "amazon_us", label: "Amazon US" },
            { value: "amazon_india", label: "Amazon India" },
            { value: "flipkart", label: "Flipkart" },
            { value: "inkscribe", label: "InkScribe" },
            { value: "kindle", label: "Kindle" },
            { value: "google_books", label: "Google Books" },
            { value: "amazon_prime", label: "Amazon Prime" },
          ],
        },
      ],
    },
    {
      title: "CTA & SEO",
      fields: [
        { key: "primary_cta_label", label: "Primary CTA label", type: "text", help: "Example: Buy Volume 1" },
        { key: "primary_cta_href", label: "Primary CTA link", type: "text" },
        { key: "seo_title", label: "SEO title", type: "text" },
        { key: "seo_description", label: "SEO description", type: "textarea" },
        { key: "seo_og_image", label: "Open Graph image", type: "image", category: "book_cover" },
        { key: "display_order", label: "Display order", type: "number" },
      ],
    },
  ],
};

/* ============ Founder ============ */
export const FOUNDER_SCHEMA = {
  title: "Founder Profile",
  collection: "founder",
  singleton: true,
  singletonKey: { field: "key", value: "main" },
  assetCategory: "founder",
  sections: [
    {
      title: "Identity",
      fields: [
        { key: "name", label: "Founder name", type: "text", required: true },
        { key: "title", label: "Title / designation line", type: "text" },
        { key: "short_bio", label: "Short bio", type: "textarea" },
        { key: "quote", label: "Founder quote", type: "textarea" },
      ],
    },
    {
      title: "Portraits",
      fields: [
        { key: "portrait_image", label: "Portrait image", type: "image", category: "founder" },
        { key: "portrait_alt", label: "Portrait alt text (accessibility)", type: "text" },
        { key: "speaking_image", label: "Event / speaking image", type: "image", category: "founder" },
        { key: "speaking_alt", label: "Speaking image alt text", type: "text" },
        { key: "speaking_caption", label: "Speaking image caption", type: "text" },
      ],
    },
    {
      title: "Biography",
      fields: [
        { key: "long_bio", label: "Long biography", type: "longtext" },
        {
          key: "sections",
          label: "Biography sections (repeatable)",
          type: "rows",
          defaultItem: { title: "", body: "" },
          addLabel: "Add bio section",
          rowFields: [
            { key: "title", label: "Section title", type: "text" },
            { key: "body", label: "Body", type: "longtext" },
          ],
        },
      ],
    },
    {
      title: "Verification & links",
      fields: [
        { key: "verification_url", label: "Verification source URL", type: "url", help: "Public-facing biography source." },
      ],
    },
    {
      title: "Awards & recognitions",
      fields: [
        {
          key: "awards",
          label: "Awards / recognitions",
          type: "rows",
          defaultItem: { title: "", publisher: "", type: "", year: "", verified: false, source_url: "" },
          addLabel: "Add award",
          rowFields: [
            { key: "title", label: "Recognition title", type: "text" },
            { key: "publisher", label: "Publisher / outlet", type: "text" },
            { key: "type", label: "Type", type: "text", help: "e.g. award, profile, advisory" },
            { key: "year", label: "Year", type: "number" },
            { key: "source_url", label: "Source URL", type: "url" },
            { key: "verified", label: "Verified", type: "switch" },
          ],
        },
        { key: "awards_note", label: "Awards note (internal)", type: "textarea" },
      ],
    },
    {
      title: "Recognitions (legacy / alternate list)",
      fields: [
        {
          key: "recognitions",
          label: "Recognitions (alternative legacy schema)",
          type: "rows",
          defaultItem: { title: "", year: "", type: "", short_description: "", proof_image: "", source_url: "", status: "published", order: 0 },
          addLabel: "Add recognition",
          rowFields: [
            { key: "title", label: "Title", type: "text" },
            { key: "year", label: "Year", type: "text" },
            { key: "type", label: "Type", type: "text" },
            { key: "short_description", label: "Short description", type: "textarea" },
            { key: "proof_image", label: "Proof image", type: "image", category: "recognition" },
            { key: "source_url", label: "Source URL", type: "url" },
            { key: "status", label: "Status", type: "select", options: STATUS_OPTIONS },
            { key: "order", label: "Display order", type: "number" },
          ],
        },
      ],
    },
    {
      title: "Media images",
      fields: [
        {
          key: "media_images",
          label: "Media images (talks, press photos)",
          type: "rows",
          defaultItem: { image: "", caption: "", source_url: "" },
          addLabel: "Add media image",
          rowFields: [
            { key: "image", label: "Image", type: "image", category: "founder" },
            { key: "caption", label: "Caption", type: "text" },
            { key: "source_url", label: "Source URL", type: "url" },
          ],
        },
        { key: "media_note", label: "Media note (internal)", type: "textarea" },
      ],
    },
    {
      title: "Related books",
      fields: [
        {
          key: "related_books",
          label: "Related book slugs",
          type: "stringRows",
          itemLabel: "Book slug",
          addLabel: "Add book slug",
          help: "Slugs from the Books module — e.g. edge-of-the-universe.",
        },
      ],
    },
  ],
};

/* ============ Founding Members ============ */
export const FOUNDING_MEMBERS_SCHEMA = {
  title: "Founding Members",
  collection: "founding_members",
  assetCategory: "founding_member",
  labelField: (d) => d.name,
  sublabelField: (d) => d.title || d.role_category || d.institution || "",
  statusOptions: STATUS_OPTIONS,
  newDefaults: { status: "published" },
  sections: [
    {
      title: "Identity",
      fields: [
        { key: "name", label: "Name", type: "text", required: true },
        { key: "alternate_name", label: "Alternate / display name", type: "text", help: "e.g. Dr. John JJ Jiang" },
        { key: "slug", label: "Slug (URL)", type: "slug" },
        { key: "title", label: "Role / title", type: "text" },
        { key: "role_category", label: "Role category", type: "text", help: "e.g. Higher Education & Interdisciplinary Leadership" },
        { key: "institution", label: "Institution / affiliation", type: "text" },
        { key: "organization", label: "Organization (alternate)", type: "text" },
        { key: "location", label: "Location", type: "text" },
        { key: "credentials", label: "Credentials", type: "text", help: "e.g. PhD, MD, Post-doc affiliations" },
        { key: "status", label: "Status", type: "select", options: STATUS_OPTIONS, required: true },
        { key: "display_order", label: "Display order", type: "number" },
      ],
    },
    {
      title: "Biography",
      fields: [
        { key: "short_bio", label: "Short bio", type: "textarea" },
        { key: "long_bio", label: "Long bio", type: "longtext" },
        {
          key: "expertise_tags",
          label: "Expertise tags",
          type: "tags",
          help: "Comma-separated. e.g. neuroscience, philosophy of mind",
        },
      ],
    },
    {
      title: "Portrait & links",
      fields: [
        { key: "image", label: "Portrait image", type: "image", category: "founding_member" },
        { key: "portrait_asset", label: "Portrait asset reference (legacy)", type: "text" },
        { key: "portrait_uploaded", label: "Portrait uploaded?", type: "switch" },
        { key: "image_note", label: "Image note (internal)", type: "textarea" },
        { key: "profile_link", label: "Public profile URL", type: "url" },
      ],
    },
    {
      title: "Foreword (for Beyond Science series)",
      fields: [
        { key: "foreword_relationship", label: "Foreword relationship", type: "text", help: "e.g. Author of foreword, Beyond Science series" },
        { key: "foreword_source", label: "Foreword source / document", type: "text" },
      ],
    },
  ],
};

/* ============ Founder Recognition (standalone, mirrors founder.recognitions) ============ */
export const FOUNDER_RECOGNITION_SCHEMA = {
  title: "Founder Recognition",
  collection: "founder_recognitions",
  assetCategory: "recognition",
  labelField: (d) => d.title,
  sublabelField: (d) => `${d.year || ""} · ${d.status || "published"}`,
  statusOptions: STATUS_OPTIONS,
  newDefaults: { status: "published" },
  sections: [
    {
      title: "Recognition",
      fields: [
        { key: "title", label: "Recognition title", type: "text", required: true },
        { key: "type", label: "Type", type: "text", help: "award, profile, advisory, etc." },
        { key: "year", label: "Year", type: "text" },
        { key: "short_description", label: "Short description", type: "textarea" },
        { key: "proof_image", label: "Proof image", type: "image", category: "recognition" },
        { key: "source_url", label: "Source URL", type: "url" },
        { key: "status", label: "Status", type: "select", options: STATUS_OPTIONS, required: true },
        { key: "display_order", label: "Display order", type: "number" },
      ],
    },
  ],
};

/* ============ Research Domains ============ */
export const RESEARCH_DOMAINS_SCHEMA = {
  title: "Research Domains",
  collection: "research_domains",
  assetCategory: "page",
  labelField: (d) => d.title,
  sublabelField: (d) => d.description ? d.description.slice(0, 80) : "",
  sections: [
    {
      title: "Domain",
      fields: [
        { key: "title", label: "Domain title", type: "text", required: true },
        { key: "slug", label: "Slug", type: "slug", required: true },
        { key: "description", label: "Description", type: "longtext" },
        { key: "icon_image", label: "Icon / image", type: "image", category: "page" },
        { key: "display_order", label: "Display order", type: "number" },
      ],
    },
  ],
};

/* ============ Research Questions / Compendium ============ */
export const RESEARCH_QUESTIONS_SCHEMA = {
  title: "Research Questions / Compendium",
  collection: "research_questions",
  labelField: (d) => d.title,
  sublabelField: (d) => d.domain_slug || "",
  statusOptions: STATUS_OPTIONS,
  newDefaults: { status: "published" },
  sections: [
    {
      title: "Question",
      fields: [
        { key: "title", label: "Frontier question", type: "textarea", required: true, rows: 2 },
        { key: "domain_slug", label: "Research domain (slug)", type: "text", help: "Must match a Research Domain slug." },
        { key: "known", label: "What is known", type: "longtext" },
        { key: "unknown", label: "What is unknown", type: "longtext" },
        { key: "challenge", label: "Research challenge", type: "longtext" },
        { key: "metrics", label: "Discriminating metrics / signals", type: "textarea" },
      ],
    },
    {
      title: "Methods & collaborators",
      fields: [
        {
          key: "methods",
          label: "Suggested methods",
          type: "stringRows",
          itemLabel: "Method",
          addLabel: "Add method",
        },
        {
          key: "collaborators",
          label: "Potential collaborators",
          type: "stringRows",
          itemLabel: "Collaborator",
          addLabel: "Add collaborator",
        },
        { key: "tags", label: "Tags", type: "tags" },
      ],
    },
    {
      title: "Visibility",
      fields: [
        { key: "status", label: "Status", type: "select", options: STATUS_OPTIONS },
        { key: "display_order", label: "Display order", type: "number" },
      ],
    },
  ],
};

/* ============ Programs ============ */
export const PROGRAMS_SCHEMA = {
  title: "Programs",
  collection: "programs",
  assetCategory: "page",
  labelField: (d) => d.title,
  sublabelField: (d) => d.status || "",
  statusOptions: PROGRAM_STATUS_OPTIONS,
  newDefaults: { status: "planned" },
  sections: [
    {
      title: "Program",
      fields: [
        { key: "title", label: "Program name", type: "text", required: true },
        { key: "slug", label: "Slug", type: "slug" },
        { key: "description", label: "Description", type: "longtext" },
        { key: "audience", label: "Audience", type: "textarea" },
        { key: "status", label: "Status", type: "select", options: PROGRAM_STATUS_OPTIONS, required: true },
        { key: "display_order", label: "Display order", type: "number" },
      ],
    },
  ],
};

/* ============ Media / Insights ============ */
export const MEDIA_SCHEMA = {
  title: "Media / Insights",
  collection: "media_items",
  assetCategory: "media",
  labelField: (d) => d.title,
  sublabelField: (d) => `${d.type || d.media_type || ""} · ${d.status || ""}`,
  statusOptions: STATUS_OPTIONS,
  newDefaults: { status: "draft", verified: false },
  sections: [
    {
      title: "Media item",
      fields: [
        { key: "title", label: "Title", type: "text", required: true },
        { key: "type", label: "Media type", type: "select", options: MEDIA_TYPE_OPTIONS, required: true },
        { key: "publisher", label: "Publisher / outlet", type: "text" },
        { key: "date", label: "Date", type: "text", help: "YYYY-MM-DD or readable date" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "image", label: "Image", type: "image", category: "media" },
        { key: "link", label: "External link", type: "url" },
        { key: "source_file", label: "Source file (internal asset path)", type: "text", help: "e.g. assets/media_award_proof/...pdf" },
        { key: "verified", label: "Verified?", type: "switch" },
        { key: "status", label: "Status", type: "select", options: STATUS_OPTIONS },
        { key: "display_order", label: "Display order", type: "number" },
      ],
    },
  ],
};

/* ============ Legal Pages ============ */
export const LEGAL_PAGES_SCHEMA = {
  title: "Legal Pages",
  collection: "legal_pages",
  labelField: (d) => d.title || d.slug,
  sublabelField: (d) => d.slug,
  statusOptions: [
    { value: "published", label: "Published" },
    { value: "draft", label: "Draft" },
  ],
  newDefaults: { status: "draft" },
  sections: [
    {
      title: "Legal page",
      fields: [
        { key: "slug", label: "Slug", type: "slug", required: true, help: "e.g. privacy-policy, terms-of-use" },
        { key: "title", label: "Page title", type: "text", required: true },
        { key: "last_updated", label: "Last updated date", type: "text", help: "e.g. 2026-02-15" },
        { key: "body", label: "Body content (rich text)", type: "longtext", required: true },
        { key: "seo.title", label: "SEO title", type: "text" },
        { key: "seo.description", label: "SEO description", type: "textarea" },
        {
          key: "status",
          label: "Status",
          type: "select",
          options: [
            { value: "published", label: "Published" },
            { value: "draft", label: "Draft" },
          ],
          required: true,
        },
      ],
    },
  ],
};

/* ============ Engage Forms Configuration ============ */
const FORM_FIELD_DEFAULT = { name: "", label: "", help: "", required: true, type: "text", options: [] };
const FORM_FIELDS_ROWS = {
  key: "fields",
  label: "Form fields",
  type: "rows",
  defaultItem: FORM_FIELD_DEFAULT,
  addLabel: "Add field",
  rowFields: [
    { key: "name", label: "Field key (internal)", type: "text", help: "e.g. full_name, email, message" },
    { key: "label", label: "Visible label", type: "text" },
    { key: "help", label: "Help text (optional)", type: "text" },
    {
      key: "type",
      label: "Type",
      type: "select",
      options: [
        { value: "text", label: "Text" },
        { value: "email", label: "Email" },
        { value: "textarea", label: "Textarea" },
        { value: "select", label: "Dropdown" },
      ],
    },
    { key: "options", label: "Dropdown options (comma separated)", type: "tags", help: "Only used when type = Dropdown." },
    { key: "required", label: "Required?", type: "switch" },
  ],
};

const FORM_BLOCK = [
  { key: "title", label: "Form title", type: "text" },
  { key: "subtitle", label: "Form subtitle", type: "textarea" },
  { key: "submit_label", label: "Submit button label", type: "text" },
  { key: "success_message", label: "Success message", type: "textarea" },
  { key: "consent_text", label: "Consent text (optional)", type: "textarea" },
  { key: "visible", label: "Show this form", type: "switch" },
  FORM_FIELDS_ROWS,
];

export const FORM_CONFIG_SCHEMA = {
  title: "Engage Forms Configuration",
  collection: "form_configs",
  singleton: true,
  singletonKey: { field: "key", value: "main" },
  sections: [
    { title: "Membership form", fields: FORM_BLOCK.map((f) => ({ ...f, key: `membership.${f.key || ""}` })) },
    { title: "Contribution form", fields: FORM_BLOCK.map((f) => ({ ...f, key: `contribution.${f.key || ""}` })) },
    { title: "Collaboration form", fields: FORM_BLOCK.map((f) => ({ ...f, key: `collaboration.${f.key || ""}` })) },
    { title: "Contact form", fields: FORM_BLOCK.map((f) => ({ ...f, key: `contact.${f.key || ""}` })) },
  ],
};

/* ============ Schema registry ============ */
export const SCHEMAS = {
  site_settings: SITE_SETTINGS_SCHEMA,
  navigation: NAVIGATION_SCHEMA,
  page_content: PAGE_CONTENT_SCHEMA,
  books: BOOKS_SCHEMA,
  founder: FOUNDER_SCHEMA,
  founder_recognitions: FOUNDER_RECOGNITION_SCHEMA,
  founding_members: FOUNDING_MEMBERS_SCHEMA,
  research_domains: RESEARCH_DOMAINS_SCHEMA,
  research_questions: RESEARCH_QUESTIONS_SCHEMA,
  programs: PROGRAMS_SCHEMA,
  media_items: MEDIA_SCHEMA,
  legal_pages: LEGAL_PAGES_SCHEMA,
  form_configs: FORM_CONFIG_SCHEMA,
};
